import React from 'react'
import { gql } from 'apollo-boost'
import * as R from 'ramda'
import { EditOrderAction } from './edit-order/service'

type Maybe<T> = T | null

export type OrderState =
  | 'created'
  | 'rejected'
  | 'accepted'
  | 'authorized'
  | 'processed'
  | 'notified'
  | 'executing'
  | 'missed'
  | 'confirmed'
  | 'picked'
  | 'delivered'
  | 'editing'
  | 'captured'
  | 'canceled'
  | 'invoiced'

export interface Order {
  uuid: string
  workflow_id: number
  workflow_node_id: string
  created_at: string
  carts: {
    restaurant_id: number
    channel_id: number
    integration_id: number
    integration_url: string
    items: OrderItem[]
    settings: OrderSettings
    extra_fields: string
    customer: {
      name: string
      email: string
      phone: string
    }
    charges: Charge[]
    cartCharges: CartCharge[]
  }
  order_workflow: {
    history: OrderWorkflowEvent[]
    logs: OrderWorkflowLog[]
    events: OrderWorkflowExtraEvent[]
  }
  adjustments: OrderAdjustment[]
  ivr_code: string
  state: OrderState
  totals: Totals
}

export interface OrderAdjustment {
  id: number
  user_id: number
  actions: string // EditOrderAction[]
  state: string
  subtotal: number
  delivery_charge: number
  discount: number
  service_charge: number
  tax: number
  tip: number
  total: number
  inserted_at: string
  user_email: string
  user_name: string
}

export interface Charge {
  id: number
  name: string
  amount: number
}

export interface CartCharge {
  id: number
  amount: number
  name: string
  parent_id: number
  taxable: number
  adjustments: null | CartCharge[]
  type: 'menu_markup' | 'charge'
  action: null | EditOrderAction
}

export interface OrderItem {
  id: number
  item_id: number
  // alias: string
  // barcode: string
  category: string
  // comments: string
  // dish_id: number
  dish_name: string
  // item_id: string
  item_total: string
  menu: string
  modifiers: OrderItemModifier[]
  name: string
  price: number
  // priority: number
  // serving_label: string
  // serving_qty: number
  size_name: string
  quantity: number
  parent_id: number | null
  adjustments: null | OrderItem[]
}

export interface OrderItemModifier {
  name: string
  options: OrderItemModifierOption[]
}

export interface OrderItemModifierOption {
  id: number
  // alias: string
  // dish_id: number
  // modifier_id: number
  name: string
  price: number
  // priority: number
  quantity: number
  total: number
}

export type ServiceType = 'PICKUP' | 'DELIVERY' | 'CURBSIDE' | 'DINEIN'

export interface OrderSettings {
  restaurant_location_address: Address
  service: {
    id: ServiceType
    label: string
    address: Address
  }
  time: {
    id: 'ASAP' | 'SCHEDULE'
    value: string
  }
  tender: {
    id: string
    label: string
  }
  tip: {
    id: string
    label: string
    value: number
  }
  curbside_info: string
}

export interface Address {
  id: number
  street: string
  block: Maybe<string>
  city: string
  state: string
  zip: string
  country: string
  cross_street: Maybe<string>
  delivery_instructions: Maybe<string>
  full: string
  lat: number
  lng: number
}

export interface NewAddress {
  block: string
  city: string
  complete_address: string
  country: string
  cross_street: string
  delivery_instructions: string
  lat: string
  lng: string
  nickname: string
  precision: string
  state: string
  street: string
  zip: string
  zip_plus: string
}

export type WorkflowActionType =
  | 'SubscribePayment'
  | 'NotifyChannel'
  | 'NotifyPOS'
  | 'NotifyLoyalty'
  | 'NotifyRDS'
  | 'NotifyGiftCard'
  | 'NotifyExternalOrder'
  | 'SubscribeRDS'
  | 'SubscribePOS'
  | 'CustomEmail'
  | 'SubscribeRestaurant'
  | 'SubscribeDragontail'
  | 'GenerateSegments'
  | 'WaitForFeedback'
  | 'CancelPay'
  | 'CancelOrder'
  | 'RestaurantServiceSMS'
  | 'ChannelEmail'
  | 'ChannelSMS'
  | 'UserEmail'
  | 'UserSMS'
  | 'AppMsg'
  | 'CustomerServiceMsg'
  | 'PrintOrder'
  | 'PromoCustomerEmailAndSMS'
  | 'PromoCustomerSMS'
  | 'PromoCustomerEmail'
  | 'RestaurantServiceEmail'
  | 'RestaurantServiceFax'
  | 'IsExternalOrder'
  | 'NotifyPrinter'

export interface OrderWorkflowEvent {
  type: WorkflowActionType
  sender: string
  outcome_event: 'success' | 'failure' | 'accept' | 'reject'
  outcome_message: string
  node_when_triggered: string
  node_when_received: string
  time: number
  start_time: string
  params: OrderWorkflowEventParam | null
}

export interface OrderWorkflowExtraEvent {
  created_at: string
  initiator: string
  name: string
  node_when_received: string
  notifications_triggered: number
  order_actions_triggered: number
}

export interface OrderWorkflowEventParam {
  event: string | null
  template: string | null
  state: string | null
  verb: string | null
}

export interface OrderWorkflowLog {
  from_node: string
  to_node: string
  transition: string
  transition_type: string
  time: string
  notifications_triggered: number
  orderActions_triggered: number
  initiator: string
}

export type Totals = { [key: string]: number }

export const OrderCtx = React.createContext<Order | null>(null)

export function applyOrderAdjustments(order: Order): Order {
  const processAdjustments = {
    carts: {
      cartCharges: processHiararchicalData,
      items: processHiararchicalData,
    },
  }
  const newOrder = R.evolve(processAdjustments, order)
  console.log('Processed Order', newOrder)
  return newOrder as Order
}

// This makes an hierarchy of charges and items
export function applyOrderAdjustmentsHierarchy(order: Order): Order {
  const processAdjustments = {
    carts: {
      cartCharges: R.compose(
        R.filter((x) => !x.parent_id),
        addHierchyAdjustments
      ),
      items: R.compose(
        R.filter((x) => !x.parent_id),
        addHierchyAdjustments
      ),
    },
  }
  const newOrder = R.evolve(processAdjustments, order)
  console.log('Processed Order', newOrder)
  return newOrder
}

type HItem = Partial<{ parent_id: number | null; id: number }>

function processHiararchicalData<T>(hitems: HItem[]): T[] {
  return hitems
    .filter((x) => !x.parent_id)
    .map(
      (c: HItem): HItem => {
        return findLastVersion(hitems, c) as CartCharge
      }
    ) as T[]
}

function findLastVersion(all: HItem[], c: HItem): HItem {
  const children = all.filter((x) => x.parent_id == c.id)
  return children.length ? findLastVersion(all, children[children.length - 1]) : c
}

type HItemAdjustments<T> = Partial<{ parent_id: number | null; id: number; adjustments: T[] }>

function addHierchyAdjustments<T>(all: HItemAdjustments<T>[]): T[] {
  return all.map(
    (i: HItemAdjustments<T>): HItemAdjustments<T> => {
      const children = addHierchyAdjustments(all.filter((x) => x.parent_id == i.id)) as T[]
      if (children.length) i.adjustments = children
      return i
    }
  ) as T[]
}

function processCartCharges(cartCharges: CartCharge[]): CartCharge[] {
  return cartCharges.map(
    (cartCharge: CartCharge): CartCharge => {
      const children = processCartCharges(cartCharges.filter((c: CartCharge) => c.parent_id == cartCharge.id))
      if (children.length) cartCharge.adjustments = children
      return cartCharge
    }
  )
}

function processCartItems(items: OrderItem[]): OrderItem[] {
  return items.map(
    (item: OrderItem): OrderItem => {
      const children = processCartItems(items.filter((i) => i.parent_id == item.id))
      if (children.length) item.adjustments = children
      return item
    }
  )
}

export const GET_ORDER = gql`
  query LoadOrder($id: ID!) {
    order(id: $id) {
      uuid
      workflow_id
      workflow_node_id
      fire_time
      pickup_time
      delivery_time
      due_time
      confirmation_time
      order_workflow {
        history {
          node_when_received
          node_when_triggered
          outcome_event
          outcome_message
          params {
            event
            state
            verb
            template
          }
          sender
          start_time
          time
          type
        }
        logs {
          from_node
          to_node
          notifications_triggered
          orderActions_triggered
          time
          transition
          transition_type
          initiator
        }
        events {
          created_at
          initiator
          name
          node_when_received
          notifications_triggered
          order_actions_triggered
        }
      }
      carts {
        restaurant_id
        channel_id
        integration_id
        integration_url
        extra_fields
        cartCharges {
          id
          name
          amount
          taxable
          parent_id
          type
        }
        charges {
          id
          name
          amount
        }
        customer {
          name
          email
          phone
          user_id
        }
        settings {
          restaurant_location_address {
            id
            street
            block
            city
            state
            zip
            country
            cross_street
            full
          }
          service {
            id
            label
            address {
              id
              street
              block
              city
              state
              zip
              country
              full
              lat
              lng
              delivery_instructions
            }
          }
          time {
            id
            value
          }
          tender {
            id
            label
          }
          tip {
            id
            label
            value
          }
          curbside_info
        }
        items {
          id
          item_id
          name
          menu
          category
          dish_name
          size_name
          quantity
          price
          item_total
          comments
          parent_id
          modifiers {
            name
            options {
              id
              name
              quantity
              price
            }
          }
        }
      }
      adjustments {
        id
        user_id
        actions
        state
        subtotal
        delivery_charge
        discount
        service_charge
        tax
        tip
        total
        inserted_at
        user_email
        user_name
        comments
      }
      ivr_code
      state
      totals {
        delivery
        discount
        hidden
        service
        subtotal
        tax
        tip
        tender_discount
        total_markup
        total
        detailedCharges {
          id
          name
          amount
          type
        }
      }
    }
  }
`

export const GET_ORDER_INFO = gql`
  query LoadOrderInfo($id: ID!) {
    order(id: $id) {
      workflow_id
      workflow_node_id
    }
  }
`
export const GET_ORDER_CARD = gql`
  query LoadOrderCard($id: ID!) {
    order(id: $id) {
      uuid
      workflow_id
      workflow_node_id
      due_time
      carts {
        restaurant_id
        customer {
          name
        }
        settings {
          service {
            id
            label
          }
          tender {
            id
            label
          }
        }
      }
      ivr_code
      state
      totals {
        total
      }
    }
  }
`
